import { useEffect, useMemo, useRef, useState } from "react";
import type { CncBatchResult, CncExportOptions, CncMachineProfile, CncSizeSystem } from "../../../shared/api-contract";
import { Icon } from "../../kit";
import { downloadBlob } from "../../../util/download";
import { useRing } from "../ringContext";
import {
  CNC_BATCH_MAX_SIZES,
  CNC_RANGE_STEPS,
  batchVerdict,
  batchZipEntries,
  batchZipName,
  cncSizeRange,
  parseCncSizes,
} from "./cncBatch";
import { formatCncSize, splitCheckId } from "./cncFormat";
import { Banner, Choice, CncSection, LabeledText, SmallButton } from "./cncUi";
import { zipStore } from "./zip";

/**
 * Sizes — the same design as one program per ring size, packed into ONE .zip
 * (`exportBandCncBatch`, contract v66, gated with the dialog on
 * `caps.ringCncV66`).
 *
 * Unlike the single program, this does not re-run on every change: a busy
 * design takes about 0.2 s a size (the viewer gives the page a turn between
 * sizes, so the ring view keeps moving). The person asks for it; any change afterwards (design, header,
 * profile, size list) marks the result out of date and locks the download
 * until it is made again, so a .zip always holds the programs for what is on
 * screen.
 */

const SYSTEM_WORD: Record<CncSizeSystem, string> = { US: "US", EU: "EU", UK: "UK", IN: "India" };
const PLACEHOLDER: Record<CncSizeSystem, string> = {
  US: "e.g. 9, 9.5, 10.75",
  EU: "e.g. 52, 54, 56",
  UK: "e.g. K, K.5, L",
  IN: "e.g. 12, 14, 16",
};

interface Made {
  key: string;
  result: CncBatchResult;
  system: CncSizeSystem;
  madeAt: Date;
}

/** The quick fill's opening range: US opens on the ring's own size ± 1; the other scales start empty. */
function startRange(system: CncSizeSystem, ringSizeUS: number) {
  return system === "US"
    ? { from: formatCncSize(Math.max(3, ringSizeUS - 1)), to: formatCncSize(Math.min(13, ringSizeUS + 1)) }
    : { from: "", to: "" };
}

export function CncBatchSection({
  opts,
  system,
  ringStamp,
  ringSizeUS,
  profile,
}: {
  /** The single program's options (null while the header is incomplete). */
  opts: CncExportOptions | null;
  /** The size scale chosen in the dialog — what every typed size is read in. */
  system: CncSizeSystem;
  /** Bumps whenever the ring changes. */
  ringStamp: number;
  ringSizeUS: number;
  profile: CncMachineProfile | null;
}) {
  const { r } = useRing();

  // One list per size system: switching systems must not reread US numbers as EU ones.
  const [lists, setLists] = useState<Partial<Record<CncSizeSystem, string>>>({});
  const text = lists[system] ?? "";
  const setText = (v: string) => setLists((m) => ({ ...m, [system]: v }));

  const [from, setFrom] = useState(() => startRange(system, ringSizeUS).from);
  const [to, setTo] = useState(() => startRange(system, ringSizeUS).to);
  const [step, setStep] = useState(() => CNC_RANGE_STEPS[system][0].id);
  const [fillError, setFillError] = useState<string | null>(null);
  // A range typed for one system means nothing in another.
  const firstSystem = useRef(true);
  useEffect(() => {
    if (firstSystem.current) { firstSystem.current = false; return; }
    const range = startRange(system, ringSizeUS);
    setFrom(range.from);
    setTo(range.to);
    setStep(CNC_RANGE_STEPS[system][0].id);
    setFillError(null);
    // Only a change of scale resets the range; the ring's size moving does not.
  }, [system]);

  const parsed = useMemo(() => parseCncSizes(text, system), [text, system]);
  const sizes = parsed.sizes;
  const ready = !!opts && sizes.length > 0 && parsed.errors.length === 0;
  const key = useMemo(
    () => (opts ? JSON.stringify({ opts, ringStamp, sizes: sizes.map((s) => s.label) }) : ""),
    [opts, ringStamp, sizes],
  );

  const [made, setMade] = useState<Made | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [zipError, setZipError] = useState<string | null>(null);
  const seq = useRef(0);
  useEffect(() => () => { seq.current++; }, []);

  const run = () => {
    if (!opts || !ready) return;
    const mine = ++seq.current;
    const runKey = key;
    setRunning(true);
    setRunError(null);
    setZipError(null);
    r.exportBandCncBatch({ ...opts, sizes: sizes.map((s) => s.value) }).then(
      (result) => {
        if (seq.current !== mine) return;
        setMade({ key: runKey, result, system, madeAt: new Date() });
        setRunning(false);
      },
      (e: Error) => {
        if (seq.current !== mine) return;
        setRunError(e?.message || "unknown error");
        setRunning(false);
      },
    );
  };

  const fresh = !!made && made.key === key && !running;
  const items = made?.result.items ?? [];
  const okCount = items.filter((it) => batchVerdict(it).kind === "ok").length;

  const fill = () => {
    const out = cncSizeRange(system, from, to, step);
    if ("error" in out) return setFillError(out.error);
    setFillError(null);
    setText(out.labels.join(", "));
  };

  const download = () => {
    if (!made || !fresh || !opts || !profile) return;
    try {
      const ctx = {
        model: opts.header.model,
        label: opts.header.label,
        system,
        profileName: profile.name,
        calibrated: profile.calibrated,
        calibratedBy: profile.calibratedBy,
        calibratedAt: profile.calibratedAt,
        madeAt: made.madeAt,
      };
      const bytes = zipStore(batchZipEntries(made.result, ctx).entries, made.madeAt);
      downloadBlob(new Blob([bytes], { type: "application/zip" }), batchZipName(ctx, made.result.items.length));
      setZipError(null);
    } catch (e) {
      setZipError(`The .zip could not be written: ${(e as Error)?.message || "unknown error"}`);
    }
  };

  return (
    <CncSection
      title="Sizes"
      aside={running ? <span className="font-body text-[11px] text-ed-text-3">Making {sizes.length} programs…</span> : null}
    >
      <span className="font-body text-[11px] text-ed-text-3">
        The same design as one program per ring size, all in one .zip — up to {CNC_BATCH_MAX_SIZES} sizes, in {SYSTEM_WORD[system]} sizes
        (the size scale chosen above). The ring on screen does not change.
      </span>

      <div className="flex flex-col gap-2 rounded-lg border border-ed-border bg-ed-panel-2 p-3" data-testid="cnc-batch-fill">
        <span className="font-body text-[11px] font-medium text-ed-text-2">Fill a range</span>
        <div className="flex items-end gap-2">
          <LabeledText testId="cnc-batch-from" label="From" value={from} onChange={setFrom} placeholder={system === "UK" ? "K" : "9"} mono />
          <LabeledText testId="cnc-batch-to" label="To" value={to} onChange={setTo} placeholder={system === "UK" ? "N" : "12"} mono />
          <SmallButton testId="cnc-batch-fill-go" onClick={fill}>Fill</SmallButton>
        </div>
        {CNC_RANGE_STEPS[system].length > 1 ? (
          <Choice<number> testId="cnc-batch-step" options={CNC_RANGE_STEPS[system]} value={step} onChange={setStep} />
        ) : null}
        {fillError ? <span className="font-body text-[11px] text-ed-red">{fillError}</span> : null}
      </div>

      <LabeledText
        testId="cnc-batch-sizes"
        label="Sizes to make"
        value={text}
        onChange={setText}
        placeholder={PLACEHOLDER[system]}
        mono
        error={parsed.errors.length ? parsed.errors.join(" ") : null}
        hint={sizes.length
          ? `${sizes.length} size${sizes.length === 1 ? "" : "s"}${parsed.repeats.length ? ` (${parsed.repeats.join(", ")} listed twice — made once)` : ""}.`
          : "Type sizes separated by commas, or fill a range above."}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SmallButton testId="cnc-batch-run" disabled={!ready || running} onClick={run}
          title={!opts ? "Enter a model number and a label first" : undefined}>
          <Icon name="playlist_play" size={14} />
          {running ? "Making…" : sizes.length ? `Make ${sizes.length} program${sizes.length === 1 ? "" : "s"}` : "Make programs"}
        </SmallButton>
        <SmallButton testId="cnc-batch-zip" tone="ink" disabled={!fresh || items.length === 0} onClick={download}
          title={made && opts ? batchZipName({ model: opts.header.model, label: opts.header.label, system }, items.length) : undefined}>
          <Icon name="folder_zip" size={14} /> Download all (.zip)
        </SmallButton>
      </div>
      {!opts ? (
        <span className="font-body text-[11px] text-ed-text-3">Enter a model number and a label first — every file is named from them.</span>
      ) : running ? (
        <span className="font-body text-[11px] text-ed-text-3">The ring stays live while the programs are made, one size after another — a busy design takes a few seconds for twenty sizes.</span>
      ) : null}

      {runError ? (
        <Banner tone="fail" icon="error" testId="cnc-batch-error">Could not make the programs: {runError}</Banner>
      ) : null}
      {zipError ? <Banner tone="fail" icon="error">{zipError}</Banner> : null}

      {made ? (
        <div className={`flex flex-col gap-1.5 ${fresh ? "" : "opacity-50"}`} data-testid="cnc-batch-results">
          <span className="font-body text-[12px] text-ed-text">
            {okCount} of {items.length} size{items.length === 1 ? "" : "s"} {okCount === 1 ? "has a program" : "have a program"}
            <span className="text-ed-text-3"> · made in {(made.result.elapsedMs / 1000).toFixed(1)} s</span>
          </span>
          {!fresh && !running ? (
            <span className="font-body text-[11px] text-ed-text-3">
              Out of date — the design, the details or the size list changed since. Make them again to download.
            </span>
          ) : null}
          <ul className="flex flex-col gap-1">
            {items.map((it, i) => {
              const v = batchVerdict(it);
              const tone = v.kind === "ok" ? "border-ed-border bg-ed-panel" : v.kind === "withheld" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50";
              const detail = v.kind === "withheld" ? v.firstFail : v.kind === "error" ? { id: null, message: v.error } : null;
              return (
                <li key={`${it.size}-${i}`} data-testid="cnc-batch-item" className={`flex flex-col gap-0.5 rounded-md border px-3 py-1.5 font-body text-[12px] ${tone}`}>
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="tabular-nums font-medium text-ed-text">{SYSTEM_WORD[made.system]} {it.size}</span>
                    <span className={v.kind === "ok" ? "text-ed-text-2" : v.kind === "withheld" ? "text-ed-red" : "text-amber-900"}>
                      {v.text}{it.cam3dm ? <span className="text-ed-text-3"> · .3dm</span> : null}
                      {it.warns.length && v.kind === "ok" ? <span className="text-ed-text-3"> · {it.warns.length} to read</span> : null}
                    </span>
                  </span>
                  {detail ? (
                    <span className={v.kind === "withheld" ? "text-ed-red" : "text-amber-900"} title={detail.id ?? undefined}>
                      {detail.message}
                      {v.kind === "withheld" && it.fails.length > 1 ? <span className="opacity-80"> (and {it.fails.length - 1} more)</span> : null}
                    </span>
                  ) : null}
                  {(it.notes ?? []).map((note) => {
                    const { id, message } = splitCheckId(note);
                    return <span key={note} className="text-ed-text-3" title={id ?? undefined}>{message}</span>;
                  })}
                </li>
              );
            })}
          </ul>
          <span className="font-body text-[11px] text-ed-text-3">
            The .zip holds each size&rsquo;s program and air cut (sizes that passed), every setup sheet and .3dm, and a list of
            which size passed. A withheld size has no program in it.
          </span>
        </div>
      ) : null}
    </CncSection>
  );
}
